"use client"

import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"

// Define types for our sparks
interface Spark {
  id: string
  startX: number
  startY: number
  endX: number
  endY: number
  duration: number
  delay: number
  length: number
  thickness: number
  opacity: number
  easing: string
  color: string
  angle: number // Store the angle for proper rotation
}

// Define possible paths for sparks to travel along
interface GridPath {
  startX: number
  startY: number
  endX: number
  endY: number
  angle: number // 30 or 150 degrees
  length: number // Store the length of the path
}

export function AnimatedIsometricGrid() {
  // Initialize all hooks unconditionally
  const [isMobile, setIsMobile] = useState(false)
  const [sparks, setSparks] = useState<Spark[]>([])
  const [viewportSize, setViewportSize] = useState({ width: 1000, height: 1000 })
  const gridPaths = useRef<GridPath[]>([])
  const maxSparks = 20
  const sparkInterval = useRef<NodeJS.Timeout | null>(null)
  const [dimensions, setDimensions] = useState({ width: 1000, height: 1000 })

  // Check for mobile device
  useEffect(() => {
    const isMobileDevice =
      typeof window !== "undefined" &&
      (window.innerWidth < 768 ||
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent.toLowerCase()))

    setIsMobile(isMobileDevice)
  }, [])

  // Handle resize consistently
  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Rest of the useEffect hooks should be called unconditionally

  // Use conditional rendering instead of early return

  // Generate grid paths based on viewport size
  const generateGridPaths = (width: number, height: number) => {
    const paths: GridPath[] = []

    // Calculate the number of lines needed to cover the viewport
    // We'll extend beyond the viewport to ensure coverage
    const extendedWidth = width * 1.5
    const extendedHeight = height * 1.5

    // For 30° lines (top-right to bottom-left)
    // tan(30°) ≈ 0.577, so for every 100px horizontally, we move 57.7px vertically
    const slope30 = Math.tan(Math.PI / 6) // 30 degrees in radians

    // Space between parallel lines (perpendicular distance)
    const lineSpacing = 100

    // Calculate how many lines we need
    const numLines30 = Math.ceil((extendedWidth + extendedHeight * slope30) / lineSpacing) + 5

    // Generate 30° lines
    for (let i = -numLines30; i < numLines30; i++) {
      // Calculate line position based on spacing
      const offset = i * lineSpacing

      // Calculate start and end points for this line
      // y = mx + b, where m = tan(30°) and b = offset
      // For 30° lines, we're solving for where these lines intersect the viewport boundaries

      // Where line intersects y = 0 (top edge): x = -b/m
      const topX = -offset / slope30

      // Where line intersects x = 0 (left edge): y = b
      const leftY = offset

      // Where line intersects y = height (bottom edge): x = (height - b) / m
      const bottomX = (height - offset) / slope30

      // Where line intersects x = width (right edge): y = m*width + offset
      const rightY = slope30 * width + offset

      // Determine actual start and end points based on which boundaries the line intersects
      let startX, startY, endX, endY

      if (topX >= 0 && topX <= width) {
        // Line intersects top edge
        startX = topX
        startY = 0
      } else if (leftY >= 0 && leftY <= height) {
        // Line intersects left edge
        startX = 0
        startY = leftY
      } else {
        // Line doesn't intersect viewport, skip it
        continue
      }

      if (bottomX >= 0 && bottomX <= width) {
        // Line intersects bottom edge
        endX = bottomX
        endY = height
      } else if (rightY >= 0 && rightY <= height) {
        // Line intersects right edge
        endX = width
        endY = rightY
      } else {
        // Line doesn't intersect viewport, skip it
        continue
      }

      // Calculate the length of the path
      const length = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2))

      paths.push({
        startX,
        startY,
        endX,
        endY,
        angle: 30,
        length,
      })
    }

    // For 150° lines (top-left to bottom-right)
    // tan(150°) ≈ -0.577, so for every 100px horizontally, we move -57.7px vertically
    const slope150 = Math.tan((5 * Math.PI) / 6) // 150 degrees in radians

    // Calculate how many lines we need
    const numLines150 = Math.ceil((extendedWidth + extendedHeight * Math.abs(slope150)) / lineSpacing) + 5

    // Generate 150° lines
    for (let i = -numLines150; i < numLines150; i++) {
      // Calculate line position based on spacing
      const offset = i * lineSpacing

      // Calculate start and end points for this line
      // y = mx + b, where m = tan(150°) and b = offset

      // Where line intersects y = 0 (top edge): x = -b/m
      const topX = -offset / slope150

      // Where line intersects x = 0 (left edge): y = b
      const leftY = offset

      // Where line intersects y = height (bottom edge): x = (height - b) / m
      const bottomX = (height - offset) / slope150

      // Where line intersects x = width (right edge): y = m*width + offset
      const rightY = slope150 * width + offset

      // Determine actual start and end points based on which boundaries the line intersects
      let startX, startY, endX, endY

      if (topX >= 0 && topX <= width) {
        // Line intersects top edge
        startX = topX
        startY = 0
      } else if (leftY >= 0 && leftY <= height) {
        // Line intersects left edge
        startX = 0
        startY = leftY
      } else {
        // Line doesn't intersect viewport, skip it
        continue
      }

      if (bottomX >= 0 && bottomX <= width) {
        // Line intersects bottom edge
        endX = bottomX
        endY = height
      } else if (rightY >= 0 && rightY <= height) {
        // Line intersects right edge
        endX = width
        endY = rightY
      } else {
        // Line doesn't intersect viewport, skip it
        continue
      }

      // Calculate the length of the path
      const length = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2))

      paths.push({
        startX,
        startY,
        endX,
        endY,
        angle: 150,
        length,
      })
    }

    return paths
  }

  // Create a new spark
  const createSpark = () => {
    if (sparks.length >= maxSparks || gridPaths.current.length === 0) return

    // Select a random path
    const path = gridPaths.current[Math.floor(Math.random() * gridPaths.current.length)]

    // Decide direction (forward or backward along the path)
    const forward = Math.random() > 0.5

    // Choose a color - mostly purple but occasionally white for variety
    const colorChoice = Math.random() > 0.8 ? "white" : "#7B39ED"

    // Calculate spark dimensions - longer in the direction of travel
    const sparkLength = 60 // Increased from 40 to 60
    const sparkThickness = 3 // Increased from 2 to 3

    const newSpark: Spark = {
      id: `spark-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      startX: forward ? path.startX : path.endX,
      startY: forward ? path.startY : path.endY,
      endX: forward ? path.endX : path.startX,
      endY: forward ? path.endY : path.startY,
      duration: 3 + Math.random() * 5, // 3-8 seconds
      delay: Math.random() * 0.5, // 0-0.5 second delay
      length: sparkLength,
      thickness: sparkThickness,
      opacity: 1.0, // Increased from 0.9 to 1.0 for maximum visibility
      easing: ["easeInOut", "easeIn", "easeOut"][Math.floor(Math.random() * 3)],
      color: colorChoice,
      angle: path.angle, // Store the angle for proper rotation
    }

    setSparks((prev) => [...prev, newSpark])

    // Remove the spark after it completes its journey
    setTimeout(
      () => {
        setSparks((prev) => prev.filter((s) => s.id !== newSpark.id))
      },
      (newSpark.duration + newSpark.delay) * 1000 + 100,
    )
  }

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    // Initial size
    handleResize()

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Update grid paths when viewport size changes
  useEffect(() => {
    gridPaths.current = generateGridPaths(viewportSize.width, viewportSize.height)
  }, [viewportSize])

  // Start creating sparks at intervals
  useEffect(() => {
    // Make sure grid paths are generated
    if (gridPaths.current.length === 0) {
      gridPaths.current = generateGridPaths(viewportSize.width, viewportSize.height)
    }

    // Create initial sparks immediately
    const initialSparkCount = Math.min(8, maxSparks)
    for (let i = 0; i < initialSparkCount; i++) {
      setTimeout(() => createSpark(), i * 200) // Stagger initial creation
    }

    // Set up interval for creating new sparks
    const interval = setInterval(() => {
      if (Math.random() > 0.3) {
        // 70% chance to create a new spark
        createSpark()
      }
    }, 600) // Slightly faster creation

    return () => {
      clearInterval(interval)
    }
  }, [viewportSize]) // Re-run when viewport size changes

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      {isMobile ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <defs>
            <pattern id="isometricGrid" width="173.2" height="100" patternUnits="userSpaceOnUse">
              {/* 30° lines (top-right to bottom-left) */}
              <line x1="0" y1="0" x2="86.6" y2="50" stroke="#CCCCCC" strokeWidth="0.8" strokeOpacity="0.6" />
              <line x1="86.6" y1="50" x2="173.2" y2="0" stroke="#CCCCCC" strokeWidth="0.8" strokeOpacity="0.6" />
              <line x1="0" y1="100" x2="86.6" y2="50" stroke="#CCCCCC" strokeWidth="0.8" strokeOpacity="0.6" />
              <line x1="86.6" y1="50" x2="173.2" y2="100" stroke="#CCCCCC" strokeWidth="0.8" strokeOpacity="0.6" />

              {/* 150° lines (top-left to bottom-right) */}
              <line x1="173.2" y1="0" x2="86.6" y2="50" stroke="#CCCCCC" strokeWidth="0.8" strokeOpacity="0.6" />
              <line x1="86.6" y1="50" x2="0" y2="0" stroke="#CCCCCC" strokeWidth="0.8" strokeOpacity="0.6" />
              <line x1="173.2" y1="100" x2="86.6" y2="50" stroke="#CCCCCC" strokeWidth="0.8" strokeOpacity="0.6" />
              <line x1="86.6" y1="50" x2="0" y2="100" stroke="#CCCCCC" strokeWidth="0.8" strokeOpacity="0.6" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#isometricGrid)" />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <defs>
            <pattern id="isometricGrid" width="173.2" height="100" patternUnits="userSpaceOnUse">
              {/* 30° lines (top-right to bottom-left) */}
              <line x1="0" y1="0" x2="86.6" y2="50" stroke="#CCCCCC" strokeWidth="1" />
              <line x1="86.6" y1="50" x2="173.2" y2="0" stroke="#CCCCCC" strokeWidth="1" />
              <line x1="0" y1="100" x2="86.6" y2="50" stroke="#CCCCCC" strokeWidth="1" />
              <line x1="86.6" y1="50" x2="173.2" y2="100" stroke="#CCCCCC" strokeWidth="1" />

              {/* 150° lines (top-left to bottom-right) */}
              <line x1="173.2" y1="0" x2="86.6" y2="50" stroke="#CCCCCC" strokeWidth="1" />
              <line x1="86.6" y1="50" x2="0" y2="0" stroke="#CCCCCC" strokeWidth="1" />
              <line x1="173.2" y1="100" x2="86.6" y2="50" stroke="#CCCCCC" strokeWidth="1" />
              <line x1="86.6" y1="50" x2="0" y2="100" stroke="#CCCCCC" strokeWidth="1" />
            </pattern>

            {/* Linear gradient for sparks */}
            <linearGradient id="sparkGradientPurple" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#7B39ED" stopOpacity="0" />
              <stop offset="15%" stopColor="#9B59FF" stopOpacity="1" /> {/* Brighter purple */}
              <stop offset="85%" stopColor="#9B59FF" stopOpacity="1" /> {/* Brighter purple */}
              <stop offset="100%" stopColor="#7B39ED" stopOpacity="0" />
            </linearGradient>

            <linearGradient id="sparkGradientWhite" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="white" stopOpacity="0" />
              <stop offset="15%" stopColor="white" stopOpacity="1" />
              <stop offset="85%" stopColor="white" stopOpacity="1" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>

            {/* Create masks for the sparks to ensure they fit the line width */}
            <mask id="sparkMask30">
              <rect x="-20" y="-1" width="40" height="2" fill="white" transform="rotate(30)" />
            </mask>

            <mask id="sparkMask150">
              <rect x="-20" y="-1" width="40" height="2" fill="white" transform="rotate(150)" />
            </mask>
          </defs>

          {/* Background grid pattern */}
          <rect width="100%" height="100%" fill="url(#isometricGrid)" />

          {/* Animated sparks */}
          <AnimatePresence>
            {sparks.map((spark) => {
              // Calculate the angle in radians for proper rotation
              const angleRad = (spark.angle * Math.PI) / 180

              // Calculate the direction vector
              const dx = spark.endX - spark.startX
              const dy = spark.endY - spark.startY

              // Calculate the exact angle based on the direction vector
              const exactAngle = Math.atan2(dy, dx) * (180 / Math.PI)

              return (
                <motion.g
                  key={spark.id}
                  initial={{
                    opacity: 0,
                    x: spark.startX,
                    y: spark.startY,
                  }}
                  animate={{
                    opacity: spark.opacity,
                    x: spark.endX,
                    y: spark.endY,
                  }}
                  exit={{
                    opacity: 0,
                    transition: { duration: 0.2 },
                  }}
                  transition={{
                    duration: spark.duration,
                    delay: spark.delay,
                    ease: spark.easing,
                  }}
                >
                  {/* Use a rectangle for the spark, rotated to match the line angle */}
                  <rect
                    width={spark.length}
                    height={spark.thickness}
                    x={-spark.length / 2}
                    y={-spark.thickness / 2}
                    fill={spark.color === "white" ? "url(#sparkGradientWhite)" : "url(#sparkGradientPurple)"}
                    style={{
                      filter: `blur(0.1px) drop-shadow(0 0 2px ${
                        spark.color === "white" ? "rgba(255,255,255,0.95)" : "rgba(155,89,255,0.95)"
                      })`,
                    }}
                    transform={`rotate(${exactAngle})`}
                    rx={spark.thickness / 2}
                    ry={spark.thickness / 2}
                  />
                </motion.g>
              )
            })}
          </AnimatePresence>
        </svg>
      )}
    </div>
  )
}
